// Core module cloning utilities
// Contains cloning functions without Firebase dependencies

import { FlexModule } from './modules';
import type {
  FormFieldConfig,
  FormFieldCondition,
  FormFieldDependency,
  FormSectionConfig,
  FlexFormTemplate
} from '@captiva/shared-types';

/**
 * Generate a unique GUID for cloned entities using crypto.randomUUID()
 */
export const generateUniqueId = (): string => {
  return crypto.randomUUID();
};

export type TemplateField = FormFieldConfig & {
  sampleOptionsId?: string;
};

export type TemplateSection = FormSectionConfig;

export interface TemplateData extends Omit<FlexFormTemplate, 'fields' | 'companyId' | 'id'> {
  id: string;
  fields: TemplateField[];
}

/**
 * Clone a template from a module with new IDs and metadata
 */
export const cloneTemplateFromModule = (
  templateData: TemplateData,
  module: FlexModule,
  companyId: string,
  userEmail: string,
  sampleOptionsMapping: Map<string, string> = new Map()
) => {
  // Clone and update field IDs
  const fieldIdMapping = new Map<string, string>(); // Map old field ID to new field ID
  const clonedFields: TemplateField[] = templateData.fields.map(field => {
    const newFieldId = generateUniqueId();
    fieldIdMapping.set(field.id, newFieldId);

    // Clone the field and update sampleOptionsId if it references a sample options set
    const clonedField: TemplateField = { ...field, id: newFieldId };

    if (field.sampleOptionsId && sampleOptionsMapping.has(field.sampleOptionsId)) {
      const firestoreId = sampleOptionsMapping.get(field.sampleOptionsId)!;
      clonedField.optionsSource = {
        type: 'firestore',
        collection: 'flexFormSelectOptions',
        document: firestoreId,
        id: firestoreId
      };
      // Remove the sampleOptionsId as it's no longer needed
      delete clonedField.sampleOptionsId;
    }

    return clonedField;
  });

  // Remap internal field references (displayIf conditions and dependencies)
  const mappedClonedFields = clonedFields.map((field: TemplateField) => {
    let updatedField: TemplateField = { ...field };

    if (Array.isArray(updatedField.displayIf)) {
      updatedField.displayIf = updatedField.displayIf.map((cond: FormFieldCondition) => ({
        ...cond,
        fieldId: fieldIdMapping.get(cond.fieldId) || cond.fieldId
      }));
    }

    if (Array.isArray(updatedField.dependencies)) {
      updatedField.dependencies = updatedField.dependencies.map((dep: FormFieldDependency) => ({
        ...dep,
        fieldId: fieldIdMapping.get(dep.fieldId) || dep.fieldId
      }));
    }

    return updatedField;
  });

  // Update section field references (if sections exist)
  const clonedSections = templateData.sections ? templateData.sections.map(section => ({
    ...section,
    fields: section.fields.map(fieldId => fieldIdMapping.get(fieldId) || fieldId)
  })) : [];

  // Create the cloned template
  const clonedTemplate = {
    ...templateData,
    companyId,
    moduleId: module.id,
    name: templateData.name,
    description: templateData.description || "",
    fields: mappedClonedFields,
    sections: clonedSections,
    active: true,
    archived: false,
    createdBy: userEmail || "module-install",
    updatedBy: userEmail || "module-install",
    // Add module metadata
    moduleMetadata: {
      originalModuleId: module.id,
      originalModuleName: module.name,
      originalTemplateId: templateData.id,
      installedBy: userEmail || "module-install",
      version: module.version || "1.0.0"
    }
  };

  return clonedTemplate;
};

interface ReportData {
  id: string;
  name: string;
  description?: string;
  includedTemplates: string[];
  dataSource: string;
  reportSelectStatement: string;
  reportTemplateLink: string;
  renderingType: string;
  reportShortName?: string;
  isActive: boolean;
  moduleName?: string;
  postProcessingType?: any;
  postProcessingParams?: any;
  enableDynamicExecution?: boolean;
  dynamicPostProcessing?: any[];
  runtimeFilters?: any[];
}

/**
 * Clone a report from a module with new IDs
 */
export const cloneReportFromModule = (
  reportData: ReportData,
  module: FlexModule,
  companyId: string,
  userEmail: string
) => {
  const clonedReport = {
    companyId,
    moduleId: module.id,
    name: reportData.name,
    description: reportData.description || "",
    includedTemplates: reportData.includedTemplates, // Will be updated with new template IDs after cloning
    dataSource: reportData.dataSource,
    reportSelectStatement: reportData.reportSelectStatement,
    reportTemplateLink: reportData.reportTemplateLink,
    renderingType: reportData.renderingType,
    reportShortName: reportData.reportShortName || reportData.name || "",
    isActive: reportData.isActive,
    moduleName: reportData.moduleName || module.name || "",
    createdBy: userEmail || "module-install",
    updatedBy: userEmail || "module-install",
    ...(reportData.postProcessingType && { postProcessingType: reportData.postProcessingType }),
    ...(reportData.postProcessingParams && { postProcessingParams: reportData.postProcessingParams }),
    ...(reportData.enableDynamicExecution !== undefined && { enableDynamicExecution: reportData.enableDynamicExecution }),
    ...(reportData.dynamicPostProcessing && { dynamicPostProcessing: reportData.dynamicPostProcessing }),
    ...(reportData.runtimeFilters && { runtimeFilters: reportData.runtimeFilters }),
    // Add module metadata
    moduleMetadata: {
      originalModuleId: module.id,
      originalModuleName: module.name,
      originalReportId: reportData.id,
      installedBy: userEmail || "module-install",
      version: module.version || "1.0.0"
    }
  };

  return clonedReport;
};

interface OptionsData {
  id?: string;
  name: string;
  description?: string;
  options?: any[];
  linkedFormTemplateId?: string;
  mappingConfig?: any;
  autoSync?: boolean;
}

/**
 * Clone an options set from a module with new IDs
 */
export const cloneOptionsSetFromModule = (
  optionsData: OptionsData,
  module: FlexModule,
  companyId: string,
  userEmail: string
) => {
  const clonedOptions = {
    name: optionsData.name,
    options: optionsData.options || [],
    metadata: {
      description: optionsData.description || "",
      originalModuleId: module.id,
      originalOptionsSetId: optionsData.id || null,
      installedBy: userEmail || "module-install"
    },
    companyId,
    moduleId: module.id,
    active: true,
    createdBy: userEmail || "module-install",
    ...(optionsData.linkedFormTemplateId && {
      linkedFormTemplateId: optionsData.linkedFormTemplateId,
      autoSync: optionsData.autoSync !== false,
    }),
    ...(optionsData.mappingConfig && { mappingConfig: optionsData.mappingConfig }),
  };

  return clonedOptions;
};